/**
 * Non-visual helpers shared by the report screens: reading the device metadata
 * blob off a report, and getting report PDFs onto the user's disk.
 */

// ------------------------------------------------------------- Device metadata

const normalize = (value, fallback = "--") =>
  String(value || fallback).replace(/_/g, " ");

export const parseDeviceMeta = (metaData) => {
  let parsed = {};

  try {
    parsed = JSON.parse(metaData || "{}") || {};
  } catch (e) {
    parsed = {};
  }

  return {
    deviceName: normalize(parsed.device_name),
    deviceId: normalize(parsed.device_id),
    firmwareVersion: normalize(parsed.firmware_version),
    appVersion: normalize(parsed.app_version),
    ecgProcessorVersion: normalize(parsed.ecg_processor_version),
    source: normalize(parsed.source, "Spandan Android App"),
  };
};

export const getLeadLabel = (reportType) =>
  reportType === "REPORT_TWELVE_LEAD" ? "12 Lead ECG" : "6 Lead ECG";

// ----------------------------------------------------------------- Downloads

// Read per call, never cached at module scope: this module is evaluated at app
// boot, which is before login writes @auth_login, so a snapshot would leave the
// permission false for the whole session and only come right after a refresh.
const getAuthData = () => {
  try {
    return JSON.parse(localStorage.getItem("@auth_login") || "{}");
  } catch (e) {
    return {};
  }
};

export const getAIReportPdfUrl = (report = {}) =>
  report?.AppreportPdfUrl ||
  report?.AppReportPdfURL ||
  report?.AppReportPdfUrl ||
  report?.appReportPdfUrl ||
  report?.appreportPdfUrl;

export const canViewAIgeneratedReports = () =>
  Boolean(getAuthData()?.data?.canViewAIgeneratedReports);

const getReportFileName = (data = {}, suffix = "") => {
  const name = [data?.id, data?.user_data?.first_name]
    .filter(Boolean)
    .join("_");
  return `${name || "report"}${suffix}.pdf`;
};

export const downloadFileFromUrl = async (url, filename) => {
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Unable to fetch file");

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = objectUrl;
    downloadLink.download = filename;
    downloadLink.target = "_blank";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(objectUrl);
  } catch (error) {
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.target = "_blank";
    downloadLink.download = filename;
    downloadLink.rel = "noopener noreferrer";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }
};

export const downloadInterpretedReport = (data, appReport) => {
  if (data.ReportPdfURL) {
    downloadFileFromUrl(data.ReportPdfURL, getReportFileName(data));
  } else if (data?.ReportPdf) {
    const linkSource = `data:application/pdf;base64,${data?.ReportPdf}`;
    const downloadLink = document.createElement("a");

    downloadLink.href = linkSource;
    downloadLink.target = "_blank";
    downloadLink.download = getReportFileName(data);
    downloadLink.click();
  } else if (appReport) {
    downloadFileFromUrl(getAIReportPdfUrl(data), getReportFileName(data));
  } else {
    console.error("No PDF URL or Base64 data available to download.");
  }
};

export const downloadAIgeneratedReport = (data = {}) => {
  const aiReportUrl = getAIReportPdfUrl(data);

  if (!aiReportUrl) {
    console.error("No AI generated PDF URL available to download.");
    return;
  }

  downloadFileFromUrl(aiReportUrl, getReportFileName(data, "_ai_generated"));
};

export const downloadPrescriptionFile = (url) => {
  if (!url) return;

  downloadFileFromUrl(url, "prescription.pdf");
};
